import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api, { API } from "@/api";
import useRevalidate from "@/lib/useRevalidate";
import { Card } from "@/components/ui/card";
import { RotateCcw, FileText } from "lucide-react";

const money = (n) => "₹" + Number(n || 0).toFixed(2);

export function ReturnList() {
  const { t } = useApp();
  const [list, setList] = useState([]);
  const load = () => api.get("/returns").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  useRevalidate(load);
  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("returns")}</div>
        <h1 className="font-heading text-3xl font-bold text-stone-900 mt-1">{t("returns")}</h1>
      </div>
      {list.length === 0 && <Card className="p-10 text-center text-stone-500" data-testid="empty-returns">{t("no_returns")}</Card>}
      <div className="space-y-2">
        {list.map((r) => (
          <Link key={r.id} to={`/returns/${r.id}`} data-testid={`return-row-${r.id}`}
                className="block p-4 rounded-2xl border border-red-200 bg-white hover:bg-red-50/40 transition-colors">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-100 grid place-items-center shrink-0">
                  <RotateCcw className="w-4 h-4 text-red-600" />
                </div>
                <div className="min-w-0">
                  <div className="font-mono font-semibold text-stone-900">{r.return_no}</div>
                  <div className="text-xs text-stone-500 truncate">{t("bill_no") || "Bill"}: <span className="font-mono">{r.bill_no}</span> · {r.customer_name || "—"}</div>
                  <div className="text-xs text-stone-500">{r.created_at?.slice(0, 16).replace("T", " ")}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono font-semibold text-lg text-red-600">−{money(r.total)}</div>
                {r.refund_amount > 0 && <div className="text-xs text-stone-600">refund {money(r.refund_amount)}</div>}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ReturnView() {
  const { t } = useApp();
  const { id } = useParams();
  const [r, setR] = useState(null);
  useEffect(() => { api.get(`/returns/${id}`).then((res) => setR(res.data)); }, [id]);
  if (!r) return null;
  const token = localStorage.getItem("token");
  const pdfUrl = `${API}/returns/${id}/pdf`;
  const viewPdf = async (e) => {
    e.preventDefault();
    const res = await fetch(pdfUrl, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };
  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("return_receipt")}</div>
          <h1 className="font-heading text-3xl font-bold text-red-600 font-mono">{r.return_no}</h1>
          <div className="text-sm text-stone-500 mt-1">
            {t("bill_no") || "Bill"}: <Link to={`/bills/${r.bill_id}`} className="font-mono text-[#B45309] font-medium">{r.bill_no}</Link>
          </div>
        </div>
        <a href={pdfUrl} onClick={viewPdf} target="_blank" rel="noreferrer" data-testid="return-view-pdf"
           className="h-12 px-5 rounded-xl bg-[#B45309] hover:bg-[#92400E] text-white font-semibold flex items-center gap-2 text-sm">
          <FileText className="w-4 h-4" /> {t("view_pdf")}
        </a>
      </div>
      <Card className="p-6 rounded-2xl">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-stone-500">
            <tr><th className="text-left py-2">{t("items")}</th><th className="text-right py-2">{t("quantity")}</th><th className="text-right py-2">{t("rate")}</th><th className="text-right py-2">{t("amount")}</th></tr>
          </thead>
          <tbody>
            {r.lines.map((l, i) => (
              <tr key={i} className="border-t border-stone-100">
                <td className="py-2"><div className="font-medium">{l.name_en}</div><div className="text-xs text-stone-500 font-gujarati">{l.name_gu}</div></td>
                <td className="py-2 text-right font-mono">{l.quantity} {l.unit}</td>
                <td className="py-2 text-right font-mono">₹{l.rate.toFixed(2)}</td>
                <td className="py-2 text-right font-mono">₹{l.total.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-6 space-y-1 text-right font-mono">
          <div className="text-2xl font-bold text-red-600">{t("return_total")}: −{money(r.total)}</div>
          <div className="text-sm text-stone-600">{t("refund_amount")}: {money(r.refund_amount)}</div>
        </div>
        {r.notes && <div className="mt-4 text-sm text-stone-600"><span className="font-bold uppercase text-xs tracking-widest text-stone-500">{t("return_notes")}: </span>{r.notes}</div>}
      </Card>
    </div>
  );
}
